import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks/shared";
import { toClassSearchBody } from "@/features/classes/shared/classFilters";
import { ClassesApi, type SearchQueryRequest } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface UseClassesGetAdminProps {
  page?: number;
  pageSize?: number;
  lastSelectedId?: number;
  active?: boolean;
  searchQueryRequest?: SearchQueryRequest | null;
  enabled?: boolean;
}

export const useClassesGetAdmin = ({
  page = 1,
  pageSize = 1000,
  lastSelectedId,
  active,
  searchQueryRequest,
  enabled = true,
}: UseClassesGetAdminProps = {}) => {
  const accessToken = useAccessToken();
  const searchBody = toClassSearchBody(searchQueryRequest);

  const { data, isSuccess, isError, isPending, isFetching, error, refetch } =
    useQuery({
    queryKey: [
      QueryKeys.Classes,
      "search",
      page,
      pageSize,
      lastSelectedId,
      active,
      searchQueryRequest,
    ],
    queryFn: async () => {
      const service = createAuthenticatedApi(ClassesApi, accessToken!);

      return service.apiClassesSearchPost({
        page,
        pageSize,
        lastSelectedId,
        active,
        searchQueryRequest: searchBody,
      });
    },
    enabled: !!accessToken && enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    throwOnError: false,
  });

  return { data, isSuccess, isError, isPending, isFetching, error, refetch };
};
